import React, { useState } from 'react';
import { PlusIcon, TagIcon } from '@heroicons/react/outline';
import TaskTagChip from './TaskTagChip';
import TaskTagSelector from './TaskTagSelector';
import { useMutation } from 'react-query';
import ApiRequest from '../../api/ApiRequest';
import { API_AUTH_TYPES, API_ROUTES } from '../../api/apiConstants';

/**
 * TaskTagList - Inline row of tag chips for a task or goal, with an add-tag button when editable.
 * Returns null when there are no tags and the list is not editable.
 *
 * @param {Object} props
 * @param {string} [props.taskId] - Task ID (used when `type` is 'task').
 * @param {string} [props.goalId] - Goal ID (used when `type` is 'goal').
 * @param {'task'|'goal'} [props.type='task'] - Whether the list is for a task or goal.
 * @param {Object[]} [props.tags=[]] - Currently assigned tag objects.
 * @param {boolean} [props.isEditable=false] - When true, shows remove buttons and an add-tag control (advisor only).
 * @param {Function} [props.onTagsChange] - Called after a tag is removed.
 * @returns {JSX.Element|null}
 */
const TaskTagList = ({ taskId, goalId, type = 'task', tags = [], isEditable = false, onTagsChange }) => {
  const [selectorOpen, setSelectorOpen] = useState(false);
  const itemLabel = type === 'goal' ? 'goal' : 'task';

  /** API client scoped to the task-tags routes for advisor access. */
  const tagsApi = new ApiRequest(API_ROUTES.TASK_TAGS, API_AUTH_TYPES.ADVISOR);

  /** Removes a single tag from the current task or goal. */
  const { mutate: removeTag } = useMutation({
    mutationFn: ({ item_id, tag_id }) => {
      if (type === 'goal') {
        return tagsApi.delete({ endpoint: 'remove_goal', params: [item_id, tag_id] });
      }
      return tagsApi.delete({ endpoint: 'remove', params: [item_id, tag_id] });
    },
    onSuccess: () => {
      onTagsChange?.();
    },
  });

  /**
   * Dispatches the removeTag mutation for the given tag on the current task or goal.
   * @param {{ id: string }} tag
   */
  const handleRemoveTag = (tag) => {
    const itemId = type === 'goal' ? goalId : taskId;
    removeTag({ item_id: itemId, tag_id: tag.id });
  };

  // Don't render anything if no tags and not editable
  if (tags.length === 0 && !isEditable) {
    return null;
  }

  return (
    <div className="relative flex flex-wrap items-center gap-1.5 mt-1">
      {/* Display existing tags */}
      {tags.map((tag) => (
        <TaskTagChip
          key={tag.id}
          tag={tag}
          showRemove={isEditable}
          onRemove={handleRemoveTag}
        />
      ))}

      {/* Add tag button (advisor only) */}
      {isEditable && (
        <button
          type="button"
          aria-label={tags.length === 0 ? `Add ${itemLabel} tag` : `Add another ${itemLabel} tag`}
          aria-haspopup="dialog"
          aria-expanded={selectorOpen}
          onClick={() => setSelectorOpen(!selectorOpen)}
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full border border-dashed border-gray-300 transition-colors"
        >
          {tags.length === 0 ? (
            <>
              <TagIcon className="w-3 h-3" aria-hidden="true" />
              <span>Add tag</span>
            </>
          ) : (
            <PlusIcon className="w-3 h-3" aria-hidden="true" />
          )}
        </button>
      )}

      {/* Tag selector dropdown */}
      {isEditable && (
        <TaskTagSelector
          taskId={taskId}
          goalId={goalId}
          type={type}
          selectedTags={tags}
          onTagsChange={onTagsChange}
          isOpen={selectorOpen}
          setIsOpen={setSelectorOpen}
        />
      )}
    </div>
  );
};

export default TaskTagList;
